import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// React Query
import { useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Grid, Link, Paper, Typography } from "@mui/material";

// Third Party
import moment from "moment";

// Custom Hooks
import useSpiffAwardSubscriptions from "./Tables/hooks/useSpiffAwardSubscriptions";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import SpiffAwardsTable from "@Motivators/components/Spiffs/Tables/SpiffAwardsTable.jsx";
import SpiffBonusEntryTable from "@Motivators/components/Spiffs/Tables/SpiffBonusEntryTable";
import SpiffEntrySectionLoader from "@Motivators/layouts/components/Skeletons/SpiffEntrySectionLoader";
import EligibilityRequirementsDialog from "./Tables/components/EligibilityRequirementsDialog";

// Contexts
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";

// API
import { getSpiffSetup } from "@Motivators/api/spiffs";

// Assets
import empty from "@/assets/images/commons/empty.png";
import { DATE_FORMAT } from "@/Constants/date";
import { useLocationCycles } from "@/Washouts/hooks/common/useLocationCycles";
import { every, isEmpty } from "lodash";

const isSpiffDateInRange = (spiffDate, startDate, endDate) => {
	const date = moment(spiffDate);
	return date.isBetween(moment(startDate), moment(endDate), null, "[]");
};

const checkIfIsEditable = (status, effectiveFrom) => {
	const currentDate = moment().startOf("day");
	const isEffectiveDateRangeFromBeforeCurrentDate = moment(effectiveFrom).isSameOrBefore(currentDate);
	return status !== "FINALIZED" && status !== "STAGING" && isEffectiveDateRangeFromBeforeCurrentDate;
};

export default function SpiffEntrySection(props) {
	const { locationId, queries, identifiers, motivatorType, selection, setSelection } = useMotivators();

	const [rulesAndDefinitionsDialogOpen, setRulesAndDefinitionsDialogOpen] = useState(false);
	const [spiffSetup, setSpiffSetup] = useState(null);
	const [spiffSetupItem, setSpiffSetupItem] = useState(null);

	const QUERY_IDENTIFIERS = [motivatorType + "Entry", "getSpiffAwards", "recipientSpiffEntry", "userStockNumbers"];
	const { clearCache } = useCacheCleaner();
	const queryClient = useQueryClient();

	const { _id: selectionId } = selection || {};
	const department = identifiers.department;
	const [commissionCycleId, spiffSetupId, spiffItemId] = selectionId ? selectionId.split("_") : [];
	const { data: commissionCycles } = useLocationCycles();

	useSpiffAwardSubscriptions({
		locationId,
		commissionCycleId,
		spiffItemId,
		department,
		clearSelection: () => setSelection(null),
		onSpiffUpdated: (updatedItem) => setSpiffSetupItem(updatedItem),
	});

	/**************************************************** QUERY ****************************************************/
	const { data, isLoading: isSpiffAwardsLoading } = useQuery({
		queryKey: [`${department}SpiffAwards`, locationId, spiffItemId],
		queryFn: async () =>
			queries.getBodyData({
				locationId,
				spiffItemId,
				department,
			}),
		enabled: every([department, locationId, spiffItemId], Boolean),
	});

	const { data: spiffSetupData, isLoading: isSpiffSetupLoading } = useQuery({
		queryKey: [`${department}SpiffSetup`, locationId, spiffSetupId],
		queryFn: async () =>
			getSpiffSetup({
				locationId,
				department,
				spiffId: spiffSetupId,
			}),
		enabled: isEmpty(data) && every([department, locationId, spiffSetupId], Boolean),
	});

	const isLoading = isSpiffAwardsLoading || isSpiffSetupLoading;
	const isSales = department === "sales";
	const isGenericSpiff = useMemo(() => spiffSetupItem?.spiffType === "Generic", [spiffSetupItem]);

	const typographyClassName = "text-neutral-gray !font-montserrat !text-[16px] !font-medium";
	const spanClassName = "!text-default";
	const typographyClass = { overflowWrap: "break-word" };

	const commissionCycle = useMemo(() => {
		if (!commissionCycleId) return null;
		return commissionCycles?.find((cc) => cc._id === commissionCycleId);
	}, [commissionCycleId, commissionCycles]);

	const awardsInCommissionCycle = useMemo(() => {
		return data?.filter((award) => award.commissionCycleId === commissionCycleId) ?? [];
	}, [data, commissionCycleId]);

	const isEditable = useMemo(() => {
		return checkIfIsEditable(commissionCycle?.status, moment(spiffSetupItem?.effectiveDateRangeFrom)?.startOf("day"));
	}, [commissionCycle, spiffSetupItem]);

	useEffect(() => {
		if (data?.length) {
			const [awardSetup] = data;
			setSpiffSetup(awardSetup.spiffSetup);
			setSpiffSetupItem(awardSetup.spiffItem);
		} else if (spiffSetupData) {
			const matchingItem = spiffSetupData.items?.find((item) => item._id === spiffItemId);
			if (matchingItem) {
				setSpiffSetup(spiffSetupData);
				setSpiffSetupItem(matchingItem);
			}
		}
	}, [data, spiffSetupData, spiffItemId]);

	const getSpiffType = () => {
		return (
			<Paper elevation={0} xs={2}>
				<Typography className={typographyClassName} component="div">
					Spiff Type: <span className={spanClassName}>{spiffSetupItem?.spiffType}</span>
				</Typography>
			</Paper>
		);
	};

	const renderCreatedBy = () => {
		return (
			<Paper elevation={0} xs={2}>
				<Typography className={typographyClassName} component="div">
					Created By: <span className={spanClassName}>{spiffSetupItem?.createdBy?.fullName}</span>
				</Typography>
			</Paper>
		);
	};

	const getEligibility = () => {
		return (
			<Paper elevation={0} xs={2} sx={{ maxWidth: "1100px" }}>
				<Typography className={typographyClassName} component="div" sx={typographyClass}>
					Eligibility:{" "}
					<span className={spanClassName}>
						{isGenericSpiff ? (
							`${spiffSetupItem?.eligibility.genericValue}`
						) : (
							<Link
								href="#"
								underline="always"
								className="!text-base !font-montserrat !font-medium !underline !text-primary !decoration-primary"
								onClick={() => setRulesAndDefinitionsDialogOpen(true)}
							>
								Rules and Tier Definitions
							</Link>
						)}
					</span>
				</Typography>
			</Paper>
		);
	};

	const getEffectiveDatesRange = () => {
		return (
			<Paper elevation={0} xs={2}>
				<Typography className={typographyClassName} component="div">
					Effective Dates:{" "}
					<span className={spanClassName}>
						{moment.utc(spiffSetupItem?.effectiveDateRangeFrom).format(DATE_FORMAT.monthAbbrDatePadded)} -{" "}
						{moment.utc(spiffSetupItem?.effectiveDateRangeTo).format(DATE_FORMAT.monthAbbrDatePadded)}
					</span>
				</Typography>
			</Paper>
		);
	};

	const renderAwardsHeader = () => {
		return (
			<>
				<Grid item xs={12} className="!mb-[20px]">
					<Grid container>
						<Paper elevation={0} xs={2} sx={{ maxWidth: "1100px" }}>
							<Typography
								className={"!font-montserrat !font-medium !text-2xl text-default"}
								component="div"
								sx={typographyClass}
							>
								{spiffSetupItem?.title}
							</Typography>
						</Paper>
					</Grid>
				</Grid>
				<Grid item xs={12} className="!mb-[30px]">
					<Grid container>
						<Grid item>
							{getSpiffType()}
							{getEligibility()}
							{getEffectiveDatesRange()}
							{isSales && (
								<Paper elevation={0} xs={2}>
									<Typography className={typographyClassName} component="div">
										Award:{" "}
										<span className={spanClassName}>
											{isGenericSpiff ? `$${(spiffSetupItem?.reward ?? 0)?.toFixed(2)}` : "Tier-Based"}
										</span>
									</Typography>
								</Paper>
							)}
							{renderCreatedBy()}
						</Grid>
					</Grid>
				</Grid>
				<EligibilityRequirementsDialog
					locationId={locationId}
					close={() => setRulesAndDefinitionsDialogOpen(false)}
					title={spiffSetup?.title}
					spiffType={spiffSetupItem?.spiffType}
					isEditMode={false}
					state={spiffSetupItem?.eligibility}
					isOpen={rulesAndDefinitionsDialogOpen}
				/>
			</>
		);
	};

	const renderServiceComponent = () => {
		if (props?.selectedItem) {
			return (
				<Grid container className="content-start h-full">
					{renderAwardsHeader()}
					<Grid item xs={12}>
						<SpiffBonusEntryTable
							data={awardsInCommissionCycle}
							loading={isLoading}
							locationId={locationId}
							dataPresent={Boolean(spiffItemId)}
							isEditable={isEditable}
							definitionId={spiffItemId}
							spiffDocumentId={spiffSetup?._id}
							effectiveDateRangeFrom={spiffSetupItem?.effectiveDateRangeFrom}
							effectiveDateRangeTo={spiffSetupItem?.effectiveDateRangeTo}
							commissionCycleId={commissionCycleId}
							key={selection?._id}
							commissionCycle={commissionCycle}
							spiffSetup={spiffSetup}
							spiffItem={spiffSetupItem}
						/>
					</Grid>
				</Grid>
			);
		} else {
			return <EmptyBody image={String(empty)} title={"No Item selected"} bodyHeightClassName={"!h-[510px]"} />;
		}
	};

	const renderSalesComponent = () => {
		if (props?.selectedItem) {
			return (
				<Grid container className="content-start h-full">
					{renderAwardsHeader()}
					<Grid item xs={12}>
						<SpiffAwardsTable
							data={awardsInCommissionCycle}
							loading={isLoading}
							locationId={locationId}
							dataPresent={Boolean(spiffItemId)}
							isEditable={isEditable}
							commissionCycle={commissionCycle}
							commissionCycleId={commissionCycleId}
							spiffSetup={spiffSetup}
							spiffItem={spiffSetupItem}
							key={selection?._id}
						/>
					</Grid>
				</Grid>
			);
		} else {
			return <EmptyBody image={String(empty)} title={"No Item selected"} bodyHeightClassName={"!h-[510px]"} />;
		}
	};

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	if (isLoading) {
		return (
			<RoundedBox sx={{ height: "680px !important" }}>
				<SpiffEntrySectionLoader entry />
			</RoundedBox>
		);
	}
	return (
		<RoundedBox sx={{ height: "680px !important" }}>
			{motivatorType === "spiffAwards" ? renderSalesComponent() : renderServiceComponent()}
		</RoundedBox>
	);
}

SpiffEntrySection.propTypes = {
	selectedItem: PropTypes.object,
};
